import styled, { css, keyframes } from 'styled-components';

import { media } from '../../../../utils/mediaQueries';

import { SLIDE_DURATION_MS } from './marketingSlides';

/* Pasek postępu przy aktywnej kropce: wypełnia się z góry na dół przez cały czas
   trwania slajdu, więc widać, kiedy pokaz przeskoczy dalej. */
const fillDown = keyframes`
  from { transform: scaleY(0); }
  to { transform: scaleY(1); }
`;

/* Nowy slajd wjeżdża od dołu razem z przesunięciem taśmy, ale tekst dochodzi chwilę
   później: dzięki temu ruch czyta się jako jedna zmiana, nie jako skok bloku. */
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

export const Slideshow = styled.section`
  display: grid;
  /* Treść, potem listwa — kolejność w DOM jest ta sama, więc tab trafia najpierw
     w slajd, a dopiero potem w przyciski nawigacji. */
  grid-template-columns: 1fr auto;
  gap: 0 26px;
  align-items: start;
  margin-bottom: 8px;
`;

/** Pionowa listwa kresek po prawej stronie pokazu. Bez numerów — rozróżnia je długość
 *  (aktywna jest dłuższa) i wypełnienie paskiem postępu. */
export const Rail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 4px;
`;

export const RailButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  appearance: none;

  &:focus-visible {
    outline: 2px solid rgba(17, 24, 39, 0.35);
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

export const RailBar = styled.span<{ $active: boolean; $done: boolean }>`
  position: relative;
  display: block;
  width: 3px;
  height: ${({ $active }) => ($active ? '46px' : '22px')};
  border-radius: 999px;
  overflow: hidden;
  background: ${({ $done }) => ($done ? '#111827' : '#e5e7eb')};
  transition: height 420ms cubic-bezier(0.16, 1, 0.3, 1), background 320ms ease;

  ${media.reducedMotion} {
    transition: none;
  }
`;

export const RailBarFill = styled.span<{ $paused: boolean }>`
  position: absolute;
  inset: 0;
  background: #111827;
  transform-origin: top center;
  transform: scaleY(0);
  animation: ${fillDown} ${SLIDE_DURATION_MS}ms linear both;
  animation-play-state: ${({ $paused }) => ($paused ? 'paused' : 'running')};

  ${media.reducedMotion} {
    animation: none;
    transform: scaleY(1);
  }
`;

export const Viewport = styled.div<{ $compact?: boolean }>`
  position: relative;
  /* Wysokość jest stała, bo slajdy mają różnej długości teksty, a skacząca kolumna
     przesuwałaby logotypy partnerów pod spodem. Wariant $compact to widok bez ramek na
     zrzuty ekranu — bez niego zostawałby pod tekstem pusty pas ~150 px. */
  height: ${({ $compact }) => ($compact ? '232px' : '372px')};
  overflow: hidden;
`;

export const Track = styled.div<{ $index: number }>`
  display: flex;
  flex-direction: column;
  height: 100%;
  transform: translateY(${({ $index }) => $index * -100}%);
  transition: transform 620ms cubic-bezier(0.16, 1, 0.3, 1);

  ${media.reducedMotion} {
    transition: none;
  }
`;

export const Slide = styled.article<{ $active: boolean }>`
  flex: 0 0 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${({ $active }) => $active
    && css`
      > * {
        animation: ${slideIn} 520ms cubic-bezier(0.16, 1, 0.3, 1) both;
      }

      > *:nth-child(2) { animation-delay: 60ms; }
      > *:nth-child(3) { animation-delay: 110ms; }
      > *:nth-child(n + 4) { animation-delay: 160ms; }
    `}

  ${media.reducedMotion} {
    > * {
      animation: none;
    }
  }
`;

export const SlideEyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;

  svg {
    width: 14px;
    height: 14px;
    color: #111827;
  }
`;

export const SlideTitle = styled.h3`
  margin: 0;
  font-size: 19px;
  font-weight: 500;
  letter-spacing: -0.01em;
  line-height: 1.35;
  color: #0b1220;
`;

export const SlideText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #4b5563;
`;

export const SlidePoints = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 2px 0 0;
  padding: 0;
  list-style: none;
`;

export const SlidePoint = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #111827;

  svg {
    width: 15px;
    height: 15px;
    flex-shrink: 0;
    color: #111827;
  }
`;

/*
 * Miejsce na zrzut ekranu. Zanim grafiki powstaną, ramka trzyma proporcje i wysokość
 * pokazu, żeby po podmianie nic się nie przesunęło. Delikatne paski w tle odróżniają
 * placeholder od pustej karty.
 */
export const MediaPlaceholder = styled.div`
  flex: 1;
  min-height: 150px;
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px dashed #d8dce3;
  border-radius: 14px;
  background-color: #fbfbfc;
  background-image: repeating-linear-gradient(
    135deg,
    rgba(17, 24, 39, 0.028) 0 8px,
    transparent 8px 16px
  );
  color: #9ca3af;

  svg {
    width: 22px;
    height: 22px;
  }
`;

export const MediaLabel = styled.span`
  font-size: 12px;
  color: #9ca3af;
`;
